/* ════════════════════════════════════════════════════════════════════
 *  MDL Studies — map real API DTOs onto the existing render shapes
 *  (ListRow + Study) so the prototype's renderers display real data
 *  unchanged. Single-analyte studies reconstruct the per-replicate spike /
 *  blank tables from the LFB/LRB columns; multi-analyte studies show the
 *  real per-parameter summary table (the prototype's synthetic per-batch
 *  matrix is not fabricated for real data).
 * ════════════════════════════════════════════════════════════════════ */
import { fmtNum, computeMatrixStats, type Study, type Analyte, type MatrixRow, type RawResultRow, type Signature, type StudyStatus, type ModelType } from './mdlCalc';
import type { ListRow, PillStatus } from './mdlData';
import type { ApiMdlListItem, ApiMdlDetail, ApiMdlReview, ApiMdlReplicate } from './api';

function isoToday(): string {
  const d = new Date(); const p = (n: number) => String(n).padStart(2, '0');
  return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate());
}

export function statusFromInt(n: number): StudyStatus {
  // Ladder (2026-06-27): 0 Pending Submission · 1 Submitted · 2 Validated · 3 Approved · 4 Released.
  // 'draft' is the internal name for the post-Submit "Submitted" state (kept to avoid renaming churn).
  return (['pending', 'draft', 'validated', 'approved', 'released'] as StudyStatus[])[n] ?? 'pending';
}
function statusLabel(n: number): PillStatus {
  return (['Pending Submission', 'Submitted', 'Validated', 'Approved', 'Released'] as PillStatus[])[n] ?? 'Pending Submission';
}
function qcPill(qc: string | null): { label: string; cls: string } | null {
  if (!qc || !qc.trim()) return null;
  const l = qc.trim();
  const cls = /pass/i.test(l) ? 'sp-pass'
    : /pending|mixed/i.test(l) ? 'sp-pending'
      : /fail|overdue/i.test(l) ? 'sp-overdue' : 'sp-pending';
  return { label: l, cls };
}

/** API list item → grid ListRow (keeps the prototype design's row shape). */
export function toListRow(a: ApiMdlListItem): ListRow {
  const nextDue = a.nextDue ?? '—';
  const overdue = !!a.nextDue && /^\d{4}-\d{2}-\d{2}$/.test(a.nextDue) && a.nextDue < isoToday();
  return {
    key: a.key,
    mdlId: a.mdlId ?? '',
    matrix: a.matrix ?? '',
    test: a.test ?? '',
    method: a.method ?? '',
    instrument: a.instrument ?? '',
    model: a.modelType === 1 ? 'Verification' : 'Initial',
    ver: a.ver ?? '',
    finalMdl: a.finalMdl ?? '—',
    spikes: String(a.spikes),
    blanks: String(a.blanks),
    days: String(a.days),
    // "Pair complete" = the spiked-replicate count matches the method-blank count
    // (feedback 2026-06-25): tick when #Spikes === #Blanks, else show a "No".
    pair: a.spikes === a.blanks,
    studyDate: a.studyDate ?? '',
    effective: a.effective ?? '—',
    nextDue,
    nextDueOverdue: overdue,
    qc: qcPill(a.qcStatus),
    active: { label: a.active ? 'Yes' : 'No', cls: a.active ? 'sp-active' : 'sp-inactive' },
    status: statusLabel(a.mdlStatus),
    createdBy: a.createdBy ?? '',
    esig: a.esigCount,
  };
}

/** A stored signature → the eSig block. Every part comes from the saved row (signer name, role,
 *  user ID, meaning, standard), so the block is complete without anything typed at sign time —
 *  the one-step Validate captures it all automatically, like the SDMS Data Package. */
export function reviewToSig(r: ApiMdlReview): Signature {
  return {
    action: r.action || '—', meaning: r.meaning || r.action || '—', name: r.reviewedBy || '—',
    userId: r.userId || '—', role: r.role || '—',
    mode: r.mode === 'part11' ? 'part11' : 'iso', mfa: !!r.mfa, when: r.reviewedDate || '',
    recordHash: r.hash || undefined, reason: r.comment || '',
  };
}

function renderRealAnalyteSummary(analytes: Analyte[], isVerif: boolean): string {
  let h = '<table class="xaf-table" style="table-layout:auto"><thead><tr>';
  h += '<th style="min-width:140px">Analyte</th><th>Unit</th><th>Spike</th><th>MDL<sub>s</sub></th><th>MDL<sub>b</sub></th><th>Final MDL</th>';
  if (isVerif) h += '<th>Current MDL</th>';
  h += '</tr></thead><tbody>';
  analytes.forEach((a) => {
    h += '<tr><td style="font-weight:bold">' + a.name + '</td><td style="color:#888">' + a.unit + '</td>';
    h += '<td class="mono">' + fmtNum(a.spike) + '</td>';
    h += '<td class="mono">' + fmtNum(a.mdls) + '</td><td class="mono">' + fmtNum(a.mdlb) + '</td>';
    h += '<td class="mono" style="font-weight:bold;color:#c45a08">' + fmtNum(a.final) + '</td>';
    if (isVerif) h += '<td class="mono">' + (a.current != null ? fmtNum(a.current) : '—') + '</td>';
    h += '</tr>';
  });
  return h + '</tbody></table>';
}

/** API detail → Study shape consumed by the existing detail renderers. */
export function toStudy(d: ApiMdlDetail): Study {
  const h = d.header;
  const results = d.results || [];
  // Always render via the matrix layout (QC Batch ID · SysSampleCode · per-analyte
  // columns + Spike Amount / Average / Count / Std Dev / T-Value rows) — including
  // single-analyte studies, so a 1-parameter study looks identical to a multi one
  // (feedback 2026-06-25). The legacy single-analyte table path is kept only for the
  // degenerate 0-result case.
  const multi = results.length >= 1;
  const isVerif = h.modelType === 1;
  const model: ModelType = isVerif ? 'verif' : 'initial';
  const units = results[0]?.units || '';
  const title = `${h.mdlId ?? ''} — ${h.test || '—'}${h.method ? ' · ' + h.method : ''}${h.instrument ? ' · ' + h.instrument : ''}`;
  const sub = `${isVerif ? 'MDL Verification' : 'Initial MDL'} · ${h.matrix || '—'} Matrix · 40 CFR 136 Appendix B, Rev 2`;

  const study: Study = {
    mdlId: h.mdlId ?? '', title, sub, modelType: model, status: statusFromInt(h.mdlStatus),
    key: h.key, kpis: [], calcRows: [], docRows: [],
  };
  study._signatures = (d.reviews || []).map(reviewToSig);
  study._attachments = (d.attachments || []).map((a) => ({ id: a.oid, name: a.name || '—', size: a.sizeBytes ?? 0, type: a.contentType || '—', when: a.date || '', by: a.operator || '—' }));

  study.docRows = [
    ['Analytical Method', h.method || '—'],
    ['Sample Matrix', h.matrix || '—'],
    ['Instrument', h.instrument || '—'],
    // Verification is driven by QC Type; Initial MDL by QC Category — show only the relevant one.
    ...(isVerif ? [['QC Type', h.qcType || '—'] as [string, string]]
                : [['QC Category', h.qcCategory || '—'] as [string, string]]),
    ['Created', (h.studyDate || '—') + (h.createdBy ? ' · ' + h.createdBy : '')],
  ];

  if (multi) {
    study.multiAnalyte = true;
    study.nSpike = h.spikes; study.nBlank = h.blanks; study.days = h.days;
    study.tValue = results[0]?.tValue ?? undefined;
    study.analytes = results.map((r) => ({
      name: r.parameter || '—', unit: r.units || '', spike: (r.spikeConc && r.spikeConc > 0) ? r.spikeConc : NaN,
      mdls: r.mdlS ?? 0, mdlb: r.mdlB ?? 0, final: r.finalMdl ?? 0,
      current: r.currentMdl ?? undefined, ratio: r.ratio ?? undefined, status: r.verification ?? undefined,
    }));
    // Rebuild the spiked/blank matrix from the normalised replicates so the
    // detail shows the same Spiked Samples / Method Blanks / Summary matrices
    // (one row per sample: AnalyticalBatchID + SampleID) as the Retrieve view.
    const resultIdx = new Map(results.map((r, i) => [r.oid, i]));
    const paramOf = new Map(results.map((r) => [r.oid, r.parameter || '—']));
    const unitOf = new Map(results.map((r) => [r.oid, r.units || '']));
    const sRowsBy = new Map<string, MatrixRow>(), bRowsBy = new Map<string, MatrixRow>();
    const rawSpike: RawResultRow[] = [], rawBlank: RawResultRow[] = [];
    for (const rp of d.replicates || []) {
      const ai = resultIdx.get(rp.resultOid); if (ai == null) continue;
      const m = rp.kind === 'S' ? sRowsBy : rp.kind === 'B' ? bRowsBy : null; if (!m) continue;
      const key = (rp.batchId || '') + '|' + (rp.sampleId || '');
      let row = m.get(key);
      if (!row) { row = { id: rp.batchId || '—', type: rp.kind === 'S' ? 'LFB' : 'LRB', sample: rp.sampleId || '—', vals: new Array(results.length).fill(NaN) }; m.set(key, row); }
      row.vals[ai] = rp.measuredValue ?? NaN;
      (rp.kind === 'S' ? rawSpike : rawBlank).push({
        sysSampleCode: rp.sysSampleCode || rp.sampleId || '', qcBatchId: rp.batchId || '', qcType: '', parameter: paramOf.get(rp.resultOid) || '—',
        numericResult: rp.measuredValue != null ? fmtNum(rp.measuredValue) : '', result: rp.resultText || '',
        units: unitOf.get(rp.resultOid) || '', analyzedDate: rp.analyzedDate || '',
      });
    }
    study._spikeRows = [...sRowsBy.values()];
    study._blankRows = [...bRowsBy.values()];
    study._rawSpike = rawSpike;
    study._rawBlank = rawBlank;
    // Spiked Samples show the raw NumericResult (reverted from % recovery, 2026-06-22).
    study._stats = computeMatrixStats(study.analytes, study._spikeRows, study._blankRows, isVerif);
    study._matrixBuilt = true;
    // KPI strip mirrors the Retrieve view (feedback 2026-06-22): Analytes · Spiked (LFB) ·
    // Blank (LRB) · Initial/Verification MDL.
    const nSpk = study._spikeRows.length, nBlk = study._blankRows.length;
    study.kpis = [
      { lbl: 'Analytes', val: String(results.length), unit: '', cls: 'blue', sub: h.test || 'Multi-analyte' },
      { lbl: 'Spiked (LFB) samples', val: String(nSpk), unit: '', cls: '', color: '#185FA5', sub: 't × S<sub>s</sub>' },
      { lbl: 'Blank (LRB) samples', val: String(nBlk), unit: '', cls: '', color: '#A32D2D', sub: 'X̄ + t × S<sub>b</sub>' },
      { lbl: isVerif ? 'MDL Verification' : 'Initial MDL', val: '40 CFR 136 B', unit: '', cls: 'green', sub: h.studyDate || h.matrix || '' },
    ];
    // Analysis-date span — reported SEPARATELY for spiked (LFB) and method-blank
    // (LRB) samples (feedback: replace single "Calendar Days" with From/To each).
    const dateSpan = (kind: 'S' | 'B') => {
      const parsed = (d.replicates || [])
        .filter((rp) => rp.kind === kind && rp.analyzedDate)
        // `split` always yields at least one element, so [0] is present — the `!` is
        // for noUncheckedIndexedAccess (tsconfig), not a runtime claim.
        .map((rp) => ({ t: new Date(rp.analyzedDate as string), day: (rp.analyzedDate as string).split(' ')[0]! }))
        .filter((x) => !isNaN(x.t.getTime()))
        .sort((a, b) => a.t.getTime() - b.t.getTime());
      if (!parsed.length) return null;
      // Guarded by the length check above; TS cannot narrow an index from `.length`.
      const lo = parsed[0]!, hi = parsed[parsed.length - 1]!;
      const days = Math.round((hi.t.getTime() - lo.t.getTime()) / 86400000) + 1;
      return { from: lo.day, to: hi.day, days };
    };
    const spikeSpan = dateSpan('S'), blankSpan = dateSpan('B');
    const spanText = (s: { from: string; to: string; days: number } | null) =>
      s ? (s.from === s.to ? s.from : s.from + ' – ' + s.to) : '—';
    const spanNote = (s: { from: string; to: string; days: number } | null, n: number) =>
      s ? s.days + ' calendar day' + (s.days === 1 ? '' : 's') + ' · ' + n + ' sample' + (n === 1 ? '' : 's') : '';
    study.calcRows = [
      ['Regulation', '40 CFR 136 Appendix B, Revision 2', ''],
      ['Study Type', (isVerif ? 'MDL Verification' : 'Initial MDL') + ' — Multi-analyte', ''],
      ['Matrix', h.matrix || '—', ''],
      ['Test / Method', `${h.test || '—'}${h.method ? ' — ' + h.method : ''}`, results.length + ' analytes'],
      ['n (spiked samples)', String(h.spikes), ''],
      ['n (method blanks)', String(h.blanks), ''],
      ['Spike Dates (LFB)', spanText(spikeSpan), spanNote(spikeSpan, h.spikes)],
      ['Blank Dates (LRB)', spanText(blankSpan), spanNote(blankSpan, h.blanks)],
      ['Final MDL', 'Per-analyte — see results table', 'Greater of MDLₛ / MDLᵇ per analyte', true],
    ];
    study.summaryHtml = renderRealAnalyteSummary(study.analytes, isVerif);
  } else {
    const r = results[0];
    const n = r?.n ?? h.spikes ?? 0;
    const df = Math.max(0, n - 1);
    const spikeConc = r?.spikeConc ?? 0;
    const reps = (d.replicates || []).filter((rp) => !r || rp.resultOid === r.oid);
    const spikes = reps.filter((rp) => rp.kind === 'S');
    const blanks = reps.filter((rp) => rp.kind === 'B');

    study.kpis = [
      { lbl: 'Final MDL', val: fmtNum(r?.finalMdl ?? 0), unit: units, cls: 'orange', sub: 'Greater of MDL<sub>s</sub> / MDL<sub>b</sub>', accent: true },
      { lbl: 'MDL<sub>s</sub> — Spiked', val: fmtNum(r?.mdlS ?? 0), unit: units, cls: 'blue', sub: 't × S<sub>s</sub>' },
      { lbl: 'MDL<sub>b</sub> — Blanks', val: fmtNum(r?.mdlB ?? 0), unit: units, cls: '', color: '#534AB7', sub: 'X̄ + t × S<sub>b</sub>' },
      { lbl: 't-value', val: fmtNum(r?.tValue ?? 0), unit: '', cls: 'green', sub: `n=${n}, single-tail` },
    ];
    const repVal = (rp: ApiMdlReplicate) => rp.measuredValue ?? 0;
    study.spikeCols = ['#', 'Batch ID', 'SysSampleCode', 'Analysis Date', `Spike Level (${units})`, `Result (${units})`, 'Recovery %', 'QC Flag'];
    study.spikeRows = spikes.map((rp, i) => {
      const sc = rp.spikeConc ?? spikeConc;
      return [i + 1, rp.batchId || '—', rp.sampleId || '—', rp.analyzedDate || '—', fmtNum(sc), fmtNum(repVal(rp)), (sc ? (repVal(rp) / sc * 100).toFixed(1) : '—') + '%', 'pass'];
    });
    study.spikeFooter = `S<sub>s</sub>: <strong style="color:#222">${fmtNum(r?.sdSpikes ?? 0)} ${units}</strong>&nbsp;&nbsp;&nbsp; t: <strong style="color:#222">${fmtNum(r?.tValue ?? 0)}</strong>&nbsp;&nbsp;&nbsp; MDL<sub>s</sub> = <strong style="color:#185FA5">${fmtNum(r?.mdlS ?? 0)} ${units}</strong>`;
    study.blankCols = ['#', 'Batch ID', 'SysSampleCode', 'Date', `Result (${units})`, 'Numerical?', 'Note'];
    study.blankRows = blanks.map((rp, i) => [i + 1, rp.batchId || '—', rp.sampleId || '—', rp.analyzedDate || '—', fmtNum(repVal(rp)), rp.numerical === false ? 'no' : 'yes', rp.note || '']);
    study.blankFooter = `MDL<sub>b</sub> = <strong style="color:#534AB7">${fmtNum(r?.mdlB ?? 0)} ${units}</strong>${r?.rule ? ' &nbsp;·&nbsp; ' + r.rule : ''}`;
    study.calcRows = [
      ['Regulation', '40 CFR 136 Appendix B, Revision 2', ''],
      ['Study Type', isVerif ? 'MDL Verification' : 'Initial MDL', ''],
      ['Matrix', h.matrix || '—', ''],
      ['Spiking Level', (h.spikeLevel || fmtNum(spikeConc)) + (units ? ' ' + units : ''), ''],
      ['n (spiked samples)', String(n), ''],
      ['Degrees of Freedom (df)', String(df), 'n − 1'],
      [`t-value (df=${df}, 99th pct, single-tail)`, fmtNum(r?.tValue ?? 0), ''],
      ['S<sub>s</sub> — Spiked std dev', `${fmtNum(r?.sdSpikes ?? 0)} ${units}`, ''],
      ['MDL<sub>s</sub> = t × S<sub>s</sub>', `${fmtNum(r?.mdlS ?? 0)} ${units}`, ''],
      ['MDL<sub>b</sub>', `${fmtNum(r?.mdlB ?? 0)} ${units}`, r?.rule || ''],
      ['Final MDL = max(MDL<sub>s</sub>, MDL<sub>b</sub>)', `${fmtNum(r?.finalMdl ?? 0)} ${units}`, '', true],
    ];
  }
  return study;
}
